import { z } from 'zod';
import { FeeTypes, type FeeType } from '@api/orderSources';

export const FEE_TYPES = [FeeTypes.FLAT, FeeTypes.PERCENTAGE] as const satisfies readonly FeeType[];

/**
 * Required fields: name, fee_type, fee.
 * Optional: description.
 *
 * Mirrors Papercut's invariant — every source has a fee_type + fee, even if
 * the fee is 0 (first-party / no-commission channels use Flat / 0).
 */
export const createOrderSourceFormSchema = z.object({
  name: z.string().trim().min(1, 'Name is required'),
  description: z
    .string()
    .trim()
    .nullable()
    .optional()
    .transform((v) => (v ? v : null)),
  fee: z.preprocess(
    // Treat empty string / null / undefined as "not provided" so z.number()'s
    // required check fires. Without this, an empty input would silently coerce
    // to 0 via Number('').
    (val) => (val === '' || val === undefined || val === null ? undefined : Number(val)),
    z
      .number({ error: 'Fee is required' })
      .min(0, 'Fee must be 0 or greater'),
  ),
  fee_type: z.enum(FEE_TYPES, { error: 'Fee type is required' }),
});

export type CreateOrderSourceFormData = z.infer<typeof createOrderSourceFormSchema>;

// Update form uses the same validation (every saved record must have fee+fee_type).
export const updateOrderSourceFormSchema = createOrderSourceFormSchema;
export type UpdateOrderSourceFormData = z.infer<typeof updateOrderSourceFormSchema>;
